// src/pages/MapPage5.js
import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  useTheme,
  useMediaQuery,
  Stepper,
  Step,
  StepLabel,
  Grid,
} from '@mui/material';
import { CSSTransition, TransitionGroup } from 'react-transition-group';
import MapChart from '../components/DataInsertion/MapChart';
import './styles/MapPage.css'; // Import CSS for animations
import { setLocalStorageItem } from '../utils/localStorage';

const MapPage = () => {
  const [activeStep, setActiveStep] = useState(0);
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [selectedOption, setSelectedOption] = useState(null);
  const navigate = useNavigate();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));
  const nodeRef = useRef(null);

  const steps = ['Select Country', 'Select Level of Analysis'];

  const handleCountrySelect = (country) => {
    setSelectedCountry(country);
    setLocalStorageItem('selectedCountry', country);
  };

  const handleOptionSelect = (option) => {
    setSelectedOption(option);
    setLocalStorageItem('selectedOption', option);
  };

  const handleNext = () => {
    if (activeStep === 0 && !selectedCountry) {
      alert('Please select a country before proceeding.');
      return;
    }
    if (activeStep === 1 && !selectedOption) {
      alert('Please select a level of analysis before proceeding.');
      return;
    }
    if (activeStep === steps.length - 1) {
      // Final step, navigate to the next page
      navigate(`/dataInsert`, { state: { selectedCountry, selectedOption } });
    } else {
      setActiveStep((prevStep) => prevStep + 1);
    }
  };

  const handleBack = () => {
    if (activeStep === 1) {
      setSelectedCountry(null); // Reset selected country when going back to Step 1
    }
    setActiveStep((prevStep) => prevStep - 1);
  };

  const renderStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              width: '100%',
            }}
          >
            <Typography variant="h5" gutterBottom>
              Step 1: Select Country
            </Typography>
            {/* Explanatory text moved above the map */}
            <Typography variant="body1" sx={{ marginBottom: theme.spacing(2) }}>
              For which you would like to perform the flexibility assessment.
            </Typography>
            <Box
              sx={{
                width: isSmallScreen ? '100%' : '90%',
                maxWidth: 1400,
                height: isSmallScreen ? '60vh' : '70vh',
                overflow: 'hidden',
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: theme.shape.borderRadius,
                padding: theme.spacing(2),
                backgroundColor: theme.palette.background.paper,
              }}
            >
              <MapChart onSelect={handleCountrySelect} />
            </Box>
            {selectedCountry && (
              <Typography variant="subtitle1" sx={{ marginTop: theme.spacing(2) }}>
                Selected Country: <strong>{selectedCountry}</strong>
              </Typography>
            )}
            {/* Navigation Buttons */}
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                gap: theme.spacing(2),
                marginTop: theme.spacing(4),
              }}
            >
              <Button
                onClick={() => navigate('/')}
                variant="text"
                sx={{
                  minWidth: 150,
                  padding: theme.spacing(1.5),
                  backgroundColor: 'white',
                  color: theme.palette.primary.main,
                  '&:hover': {
                    backgroundColor: theme.palette.action.hover,
                  },
                }}
              >
                Back
              </Button>
              <Button
                onClick={handleNext}
                variant="contained"
                disabled={activeStep === 0 && !selectedCountry}
                sx={{
                  minWidth: 150,
                  padding: theme.spacing(1.5),
                }}
              >
                Next
              </Button>
            </Box>
          </Box>
        );
      case 1:
        return (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              width: '100%',
            }}
          >
            <Typography variant="h5" gutterBottom>
              Step 2: Select Level of Analysis
            </Typography>
            <Grid
              container
              spacing={3}
              justifyContent="center"
              sx={{ marginTop: theme.spacing(2), marginBottom: theme.spacing(4) }}
            >
              <Grid item>
                <Box sx={{ width: 250, textAlign: 'center' }}>
                  <Button
                    variant="contained"
                    sx={{
                      width: '100%',
                      height: 100,
                      borderRadius: theme.shape.borderRadius,
                      fontSize: '1.2rem',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      whiteSpace: 'normal',
                      backgroundColor:
                        selectedOption === 'Aggregated'
                          ? theme.palette.secondary.main
                          : theme.palette.primary.main,
                      color: selectedOption === 'Aggregated' ? 'black' : 'white',
                      '&:hover': {
                        backgroundColor:
                          selectedOption === 'Aggregated'
                            ? theme.palette.secondary.dark
                            : theme.palette.primary.dark,
                      },
                    }}
                    onClick={() => handleOptionSelect('Aggregated')}
                  >
                    Aggregated
                  </Button>
                  <Typography variant="body2" sx={{ marginTop: theme.spacing(1) }}>
                    For the entire energy system.
                  </Typography>
                </Box>
              </Grid>
              <Grid item>
                <Box sx={{ width: 250, textAlign: 'center' }}>
                  <Button
                    variant="contained"
                    sx={{
                      width: '100%',
                      height: 100,
                      borderRadius: theme.shape.borderRadius,
                      fontSize: '1.2rem',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      whiteSpace: 'normal',
                      backgroundColor:
                        selectedOption === 'Disaggregated'
                          ? theme.palette.secondary.main
                          : theme.palette.primary.main,
                      color: selectedOption === 'Disaggregated' ? 'black' : 'white',
                      '&:hover': {
                        backgroundColor:
                          selectedOption === 'Disaggregated'
                            ? theme.palette.secondary.dark
                            : theme.palette.primary.dark,
                      },
                    }}
                    onClick={() => handleOptionSelect('Disaggregated')}
                  >
                    Disaggregated
                  </Button>
                  <Typography variant="body2" sx={{ marginTop: theme.spacing(1) }}>
                    For individual devices and components.
                  </Typography>
                </Box>
              </Grid>
            </Grid>
            {/* Navigation Buttons */}
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                gap: theme.spacing(2),
                marginTop: theme.spacing(2),
              }}
            >
              <Button
                disabled={activeStep === 0}
                onClick={handleBack}
                variant="text"
                sx={{
                  minWidth: 150,
                  padding: theme.spacing(1.5),
                  backgroundColor: 'white',
                  color: theme.palette.primary.main,
                  '&:hover': {
                    backgroundColor: theme.palette.action.hover,
                  },
                }}
              >
                Back
              </Button>
              <Button
                onClick={handleNext}
                variant="outlined"
                sx={{
                  minWidth: 150,
                  padding: theme.spacing(1.5),
                  backgroundColor: 'white',
                  color: theme.palette.primary.main,
                  '&:hover': {
                    backgroundColor: theme.palette.secondary.main,
                  },
                }}
              >
                Next
              </Button>
            </Box>
          </Box>
        );
      default:
        return 'Unknown Step';
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh', // Use minHeight so content pushes footer down
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Box sx={{ marginTop: theme.spacing(4) }}>
        <Stepper activeStep={activeStep} alternativeLabel>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel
                optional={
                  label === steps[steps.length - 1] && selectedCountry && selectedOption ? (
                    <Typography variant="caption">Ready to proceed</Typography>
                  ) : null
                }
              >
                {label}
              </StepLabel>
            </Step>
          ))}
        </Stepper>
      </Box>
      <Box sx={{ flexGrow: 1, padding: theme.spacing(2) }}>
        <TransitionGroup>
          <CSSTransition
            key={activeStep}
            nodeRef={nodeRef}
            classNames="step"
            timeout={500}
          >
            <Box
              ref={nodeRef}
              sx={{ width: '100%', height: '100%', overflowY: 'auto' }}
            >
              {renderStepContent(activeStep)}
            </Box>
          </CSSTransition>
        </TransitionGroup>
      </Box>
    </Box>
  );
};

export default MapPage;